<template>
  <ThePage class="max-md:pb-72" :title="i18n(translations.title)">
    <template #pageTitle>
      {{ i18n(translations.title) }}
    </template>
    <template #pageContent>
      <p class="mt-16">{{ i18n(translations.subTitle) }}</p>
      <div class="mt-32">
        <RevInputText
          id="search"
          v-model="searchQuery"
          class="w-full"
          :icon="IconSearch"
          :label="i18n(translations.searchInputLabel)"
          :placeholder="i18n(translations.searchInputPlaceholder)"
          type="text"
        />

        <div
          v-if="showWelcomeScreen"
          class="mt-72 flex flex-col items-center text-center"
        >
          <RevIllustration
            alt="illustration-device-smartphone"
            aria-hidden="true"
            :height="80"
            src="/img/buyback/illustration-device-smartphone.svg"
            :width="160"
          />
          <h2 class="heading-1 mt-12">
            {{ i18n(translations.noSearchTitle) }}
          </h2>
        </div>
        <div v-if="pending" class="mt-32 w-full">
          <RevLoadingScreen
            class="mx-auto mt-9"
            data-test="loading-screen"
            :text="i18n(translations.searchLoading)"
          />
        </div>
        <div v-if="showResults" class="mt-32 flex">
          <SearchFilters :facet-filters :facets @filter="handleSearchFilters" />
          <div class="flex grow flex-col md:ml-32">
            <h2 class="heading-2" data-test="results-title">
              {{
                i18n(translations.resultsTitle, {
                  count: nbHits,
                  search: searchQuery,
                })
              }}
            </h2>
            <ProductDetails
              v-for="product in products"
              :id="product.id"
              :key="product.id"
              :attributes="product.attributes"
              class="mt-32"
              :image="product.image"
              :title="product.title"
            />

            <RevPagination
              class="mt-32"
              :current-page
              :current-page-aria-label="
                i18n(paginationTranslations.page, {
                  page: currentPage,
                })
              "
              :current-page-label="
                i18n(paginationTranslations.page, {
                  page: currentPage,
                })
              "
              :navigation-aria-label="
                i18n(paginationTranslations.paginationLabel)
              "
              :next-aria-label="i18n(paginationTranslations.paginationNextPage)"
              :page-aria-label="
                (page) => i18n(paginationTranslations.navigatePage, { page })
              "
              :page-count="nbPages"
              :pages
              :previous-aria-label="
                i18n(paginationTranslations.paginationPreviousPage)
              "
              @click="handlePagination"
            />
          </div>
        </div>
        <RevCard
          v-if="showNoResult"
          class="mt-32 flex items-center p-24 md:space-x-7"
        >
          <div class="flex grow flex-col">
            <h2 class="heading-2">
              {{ i18n(translations.searchNoResultsTitle) }}
            </h2>
            <p>
              {{ i18n(translations.searchNoResultsText) }}
            </p>
          </div>
          <RevIllustration
            alt="illustration-device-smartphone"
            aria-hidden="true"
            :height="448"
            src="/img/buyback/no-result-illustration.svg"
            :width="252"
          />
        </RevCard>
      </div>
    </template>
  </ThePage>
</template>

<script setup lang="ts">
import { navigateTo, useRoute } from '#imports'
import { computed, watch } from 'vue'
import type { LocationQuery } from 'vue-router'

import { getBuyBackProducts } from '@backmarket/http-api/src/api-specs-buyback/seller/buyback-products'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useI18nLocale } from '@backmarket/nuxt-module-i18n/useI18nLocale'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevCard } from '@ds/components/Card'
import { RevIllustration } from '@ds/components/Illustration'
import { RevInputText } from '@ds/components/InputText'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevPagination } from '@ds/components/Pagination'
import { IconSearch } from '@ds/icons/IconSearch'
import pDebounce from 'p-debounce'

import paginationTranslations from '~/components/Pagination/Pagination.translations'
import { ROUTE_NAMES } from '~/constants/routes'
import { productAlgoliaHitsAdapter } from '~/scopes/buyback/pages/search-product/SearchProductAdapter'

import translations from './SearchProduct.translations'
import ProductDetails from './components/ProductDetails/ProductDetails.vue'
import SearchFilters from './components/SearchFilters/SearchFilters.vue'

const DEBOUNCE_DELAY = 300
const i18n = useI18n()
const logger = useLogger()
const route = useRoute()

const currentLocale = useI18nLocale()
const { openErrorToast } = useTheToast()

const searchBuyBackProduct = pDebounce((event: Event) => {
  const target = event.target as HTMLInputElement
  if (isEmpty(target.value)) {
    navigateTo({
      name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
      query: {},
    })
  } else {
    navigateTo({
      name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
      query: {
        ...route.query,
        query: target.value,
        lang: currentLocale,
        page: 1,
      },
    })
  }
}, DEBOUNCE_DELAY)

function handleSearchFilters(filters: LocationQuery) {
  const facetFilters = (!isEmpty(filters) && JSON.stringify(filters)) || {}

  const query = Object.entries({ ...route.query, facetFilters }).reduce(
    (acc, entries) => {
      const [key, value] = entries

      return {
        ...acc,
        ...(!isEmpty(value) ? { [key]: value } : {}),
        page: route.query.page,
      }
    },
    {},
  )

  navigateTo({
    name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
    query,
  })
}

function handlePagination({ page }: { page: number }) {
  navigateTo({
    name: ROUTE_NAMES.BUYBACK.SEARCH_PRODUCT,
    query: {
      ...route.query,
      lang: currentLocale,
      page,
    },
  })
}

const paginationFilter = computed(() => {
  const queryPageValue = Number(route.query.page)

  return route.query && queryPageValue > 0 ? queryPageValue - 1 : 0
})

const searchQuery = computed({
  get: () => route.query.query as string,
  set: (value: string) => {
    const event = { target: { value } } as unknown as Event
    searchBuyBackProduct(event)
  },
})

const { data, error, pending, refresh } = useHttpFetch(getBuyBackProducts, {
  queryParams: computed(() => ({
    ...route.query,
    page: paginationFilter.value,
  })),
})

watch(searchQuery, () => {
  refresh()
})

const nbHits = computed(() => data.value?.nbHits || 0)
const nbPages = computed(() => data.value?.nbPages || 0)
const currentPage = computed(() => (data.value?.page || 0) + 1)
const facets = computed(() => data.value?.facets || {})
const facetFilters = computed(() => {
  const facetFiltersQuery = route.query.facetFilters
  if (typeof facetFiltersQuery === 'string') {
    try {
      return JSON.parse(facetFiltersQuery)
    } catch (e) {
      return {}
    }
  }

  return {}
})

const pages = computed(() => {
  return Array.from({ length: nbPages.value }, (_, k) => ({
    label: `${k + 1}`,
    page: k + 1,
  }))
})

const products = computed(() =>
  data.value ? productAlgoliaHitsAdapter(data.value.hits) : null,
)
const hasQuery = computed(() => !isEmpty(searchQuery.value))
const showWelcomeScreen = computed(() => !pending.value && !hasQuery.value)
const showResults = computed(
  () => !pending.value && hasQuery.value && nbHits.value > 0,
)
const showNoResult = computed(
  () => !pending.value && hasQuery.value && nbHits.value === 0,
)

if (error.value) {
  openErrorToast()

  logger.error('[BUYBACK][API][SEARCH_PRODUCTS] Error getting products', {
    error: error.value,
    owners: ['bot-squad-circularity-offer-front'],
  })
}
</script>
